import { useMemo } from 'react';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { Card } from 'shared/ui/card/card';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { useRuleStats } from 'shared/hooks/useRulesStat';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import Button from 'shared/ui/button/button';
import { ProgressBar } from 'shared/ui/progress-bar/progress-bar';
import { UnitSlot } from 'shared/ui/unit-slot/unit-slot';
import { RunBlockKey, useReportProgress } from 'shared/hooks/useReportProgress';
import { getSectionStatus } from 'shared/helpers/runSectionStatus';
import { getSectionTiming } from 'shared/helpers/reportTimeline';
import cls from './improvement-overview.module.scss';

interface ImprovementOverviewProps {
  technicalRef: React.RefObject<HTMLDivElement | null>;
  completenessRef: React.RefObject<HTMLDivElement | null>;
  productsRef: React.RefObject<HTMLDivElement | null>;
  onScrollToSection: (ref: React.RefObject<HTMLDivElement | null>) => void;
}

export const ImprovementOverview: React.FC<ImprovementOverviewProps> = ({
  technicalRef,
  completenessRef,
  productsRef,
  onScrollToSection,
}) => {
  const { t } = useTranslation();
  const { selectedProject, isTrialExpired } = useCachedSelectedProject();
  const {
    productsStat,
    completenessStat,
    technicalStat,
    productsLoading,
    completenessLoading,
    technicalLoading,
    overviewLoading,
    hasTechRules,
    showTechnicalSection,
  } = useRuleStats();

  const overallStats = useMemo(
    () => ({
      total: technicalStat.total + completenessStat.total + productsStat.total,
      pass: technicalStat.pass + completenessStat.pass + productsStat.pass,
      needFix:
        technicalStat.needFix + completenessStat.needFix + productsStat.needFix,
    }),
    [
      technicalStat.total,
      completenessStat.total,
      completenessStat.pass,
      completenessStat.needFix,
      productsStat.total,
      productsStat.pass,
      productsStat.needFix,
      technicalStat.pass,
      technicalStat.needFix,
    ]
  );

  const nextStep = useMemo(() => {
    if (hasTechRules && technicalStat.needFix > 0) {
      return {
        ref: technicalRef,
        title: 'technicalAccessibilityOverview',
        description: 'nextStepTechnicalDescription',
      };
    }
    if (completenessStat.needFix > 0) {
      return {
        ref: completenessRef,
        title: 'businessInfoCompleteness',
        description: 'nextStepCompletenessDescription',
      };
    }
    if (productsStat.needFix > 0) {
      return {
        ref: productsRef,
        title: 'productsAndServices',
        description: 'nextStepProductsDescription',
      };
    }
    return null;
  }, [
    hasTechRules,
    technicalStat.needFix,
    completenessStat.needFix,
    productsStat.needFix,
    technicalRef,
    completenessRef,
    productsRef,
  ]);

  const { blocks, blockStartedAt, isRunActive, allComplete } =
    useReportProgress();
  const everythingReady = !isRunActive || allComplete;
  const summaryVisible = !overviewLoading && everythingReady;

  const technicalBlockKeys: RunBlockKey[] = [
    'robotsTxt',
    'llmsTxt',
    'schemaOrg',
  ];
  const completenessBlockKeys: RunBlockKey[] = [
    'businessName',
    'businessAddress',
    'targetAudience',
    'geographicFocus',
  ];
  const productsBlockKeys: RunBlockKey[] = [
    'conversationalWriting',
    'statisticalEvidence',
    'contentFreshness',
  ];

  const technicalWidgetStatus = getSectionStatus(
    technicalBlockKeys.map(key => blocks[key])
  );
  const completenessWidgetStatus = getSectionStatus(
    completenessBlockKeys.map(key => blocks[key])
  );
  const productsWidgetStatus = getSectionStatus(
    productsBlockKeys.map(key => blocks[key])
  );

  const renderWidget = (
    status: (typeof blocks)[keyof typeof blocks],
    label: string,
    content: React.ReactNode,
    blockKeys: RunBlockKey[]
  ) => {
    if (!isRunActive) {
      return content;
    }

    const timing = getSectionTiming(blockKeys, blockStartedAt);

    return (
      <UnitSlot
        unitState={status}
        name={label}
        minHeight={224}
        className={cls.sectionCardSlot}
        startedAt={timing.startedAt}
        expectedSeconds={timing.expectedSeconds}
      >
        {content}
      </UnitSlot>
    );
  };

  if (!selectedProject) {
    return <Skeleton height={320} />;
  }

  return (
    <Card className={cls.overviewCard}>
      <div className={cls.cardHeader}>
        <div className={cls.title}>
          <h2>{t('improvementOverview')}</h2>
        </div>
        {summaryVisible && (
          <p className={cls.overviewSubtitle}>
            {isTrialExpired ? (
              <>
                <span>{t('yourProjectMeetsPrefix')} </span>
                <Icon pathToIcon={ICONS.lockGray} size={16} />{' '}
                <strong>{t('of')}</strong>{' '}
                <Icon pathToIcon={ICONS.lockGray} size={16} />
                <span> {t('yourProjectMeetsSuffix')}</span>
              </>
            ) : (
              <>
                {t('yourProjectMeetsPrefix')}{' '}
                <strong>{overallStats.pass}</strong> <strong>{t('of')}</strong>{' '}
                <strong>{overallStats.total}</strong>{' '}
                {t('yourProjectMeetsSuffix')}
              </>
            )}
          </p>
        )}
        {!summaryVisible ? null : isTrialExpired ? (
          <p className={cls.rulesNeedFixText}>
            <Icon pathToIcon={ICONS.lockGray} size={16} />{' '}
            <strong> {t('rules')}</strong> {t('stillRequireImprovements')}
          </p>
        ) : (
          <p className={cls.rulesNeedFixText}>
            <strong>
              {overallStats.needFix} {t('rules')}
            </strong>{' '}
            {t('stillRequireImprovements')}
          </p>
        )}
      </div>

      <div
        className={`${cls.sectionsGrid} ${!isTrialExpired && nextStep ? cls.sectionsGridWithFooter : ''}`}
      >
        {showTechnicalSection &&
          renderWidget(
            technicalWidgetStatus,
            t('technicalAccessibilityOverview'),
            <div className={cls.sectionCard}>
              <h4>{t('technicalAccessibilityOverview')}</h4>
              {isTrialExpired ? (
                <div className={cls.lockIcon}>
                  <Icon pathToIcon={ICONS.lockGray} size={16} />
                </div>
              ) : technicalLoading && !isRunActive ? (
                <Skeleton height={44} width={80} />
              ) : (
                <p className={cls.sectionScore}>
                  {technicalStat.pass}/{technicalStat.total}
                </p>
              )}
              {technicalLoading && !isRunActive ? (
                <Skeleton height={8} className={cls.progressWrapper} />
              ) : isTrialExpired ? (
                <ProgressBar score={0} progressClass={cls.progressBar} />
              ) : (
                <ProgressBar
                  score={(technicalStat.pass / technicalStat.total) * 100}
                  variant='detailed'
                />
              )}
              {technicalLoading && !isRunActive ? (
                <Skeleton height={20} width={100} className={cls.marginTop8} />
              ) : isTrialExpired ? (
                <p className={cls.needFixLabel}>
                  <Icon pathToIcon={ICONS.lockGray} size={16} /> {t('needFix')}
                </p>
              ) : (
                <p className={cls.needFixLabel}>
                  {technicalStat.needFix} {t('needFix')}
                </p>
              )}
              <Button
                theme='tonal'
                tone='default'
                onClick={() => onScrollToSection(technicalRef)}
              >
                {t('viewRules')}
              </Button>
            </div>,
            technicalBlockKeys
          )}

        {renderWidget(
          completenessWidgetStatus,
          t('businessInfoCompleteness'),
          <div className={cls.sectionCard}>
            <h4>{t('businessInfoCompleteness')}</h4>
            {isTrialExpired ? (
              <div className={cls.lockIcon}>
                <Icon pathToIcon={ICONS.lockGray} size={16} />
              </div>
            ) : completenessLoading && !isRunActive ? (
              <Skeleton height={44} width={80} />
            ) : (
              <p className={cls.sectionScore}>
                {completenessStat.pass}/{completenessStat.total}
              </p>
            )}
            {completenessLoading && !isRunActive ? (
              <Skeleton height={8} className={cls.progressWrapper} />
            ) : isTrialExpired ? (
              <ProgressBar score={0} progressClass={cls.progressBar} />
            ) : (
              <ProgressBar
                score={(completenessStat.pass / completenessStat.total) * 100}
                variant='detailed'
              />
            )}
            {completenessLoading && !isRunActive ? (
              <Skeleton height={20} width={100} className={cls.marginTop8} />
            ) : isTrialExpired ? (
              <p className={cls.needFixLabel}>
                <Icon pathToIcon={ICONS.lockGray} size={16} /> {t('needFix')}
              </p>
            ) : (
              <p className={cls.needFixLabel}>
                {completenessStat.needFix} {t('needFix')}
              </p>
            )}
            <Button
              theme='tonal'
              tone='default'
              onClick={() => onScrollToSection(completenessRef)}
            >
              {t('viewRules')}
            </Button>
          </div>,
          completenessBlockKeys
        )}

        {renderWidget(
          productsWidgetStatus,
          t('productsAndServices'),
          <div className={cls.sectionCard}>
            <h4>{t('productsAndServices')}</h4>
            {isTrialExpired ? (
              <div className={cls.lockIcon}>
                <Icon pathToIcon={ICONS.lockGray} size={16} />
              </div>
            ) : productsLoading && !isRunActive ? (
              <Skeleton height={44} width={80} />
            ) : (
              <p className={cls.sectionScore}>
                {productsStat.pass}/{productsStat.total}
              </p>
            )}
            {productsLoading && !isRunActive ? (
              <Skeleton height={8} className={cls.progressWrapper} />
            ) : isTrialExpired ? (
              <ProgressBar
                score={0}
                progressClass={cls.progressBar}
                variant='simple'
              />
            ) : (
              <ProgressBar
                score={(productsStat.pass / productsStat.total) * 100}
                variant='detailed'
              />
            )}
            {productsLoading && !isRunActive ? (
              <Skeleton height={20} width={100} className={cls.marginTop8} />
            ) : isTrialExpired ? (
              <p className={cls.needFixLabel}>
                <Icon pathToIcon={ICONS.lockGray} size={16} /> {t('needFix')}
              </p>
            ) : (
              <p className={cls.needFixLabel}>
                {productsStat.needFix} {t('needFix')}
              </p>
            )}
            <Button
              theme='tonal'
              tone='default'
              onClick={() => onScrollToSection(productsRef)}
            >
              {t('viewRules')}
            </Button>
          </div>,
          productsBlockKeys
        )}
      </div>

      {!isTrialExpired && nextStep && everythingReady && (
        <div className={cls.cardFooter}>
          <div className={cls.nextStepContent}>
            <p className={cls.nextStepTitle}>
              {t('nextStepFixLabel', '_global', '', {
                title: t(nextStep.title),
              })}
            </p>
            <p className={cls.nextStepDescription}>{t(nextStep.description)}</p>
          </div>
          <Button
            theme='filled'
            tone='default'
            onClick={() => onScrollToSection(nextStep.ref)}
          >
            {t('goTo')} {t(nextStep.title)}
          </Button>
        </div>
      )}
    </Card>
  );
};
